import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Bot, Zap, Activity, Bug, Package, PlayCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import MainLayout from '../Layout/MainLayout';
import SaleV2ChatMessages from './SaleV2ChatMessages';
import V2ChatInput from '../V2Chat/V2ChatInput';
import V2EventLog from '../V2Chat/V2EventLog';
import { useSaleWorkflow } from './hooks/useSaleWorkflow';

/**
 * Sale Order Agent Chat Page
 * Uses same V2 chat system as Bill Processing for consistent tool call display
 * Backend routing (shared_engine.py) handles Sale Order specific logic
 */
const SaleOrderAgentChat = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  // Get workflow info from URL
  const workflowId = 15; // Sale Order workflow ID
  const initialRunId = searchParams.get('runId') || searchParams.get('run_id');
  const workflowName = 'Sales Order Processing';

  // State management
  const [showEventLog, setShowEventLog] = useState(false);
  const [draftData, setDraftData] = useState(null);
  const [isResuming, setIsResuming] = useState(false);
  const [resumeTrigger, setResumeTrigger] = useState(0);

  // Sale Workflow hook - handles all Sale Order workflow logic
  // Uses separate /api/sale/workflows endpoints
  const {
    messages,
    isLoading,
    runId,
    executionStatus,
    currentStep,
    sendMessage,
    events,
    error,
    resetWorkflow,
    hasActiveWorkflow,
    stopPolling,
    resumePolling
  } = useSaleWorkflow(workflowId, token, initialRunId);

  // Cleanup on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (hasActiveWorkflow) {
        console.log('🧹 Page unloading - cleaning up workflow');
        resetWorkflow();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasActiveWorkflow, resetWorkflow]);

  // Handle back navigation
  const handleBack = () => {
    navigate('/workflows');
  };

  // Handle debug - navigate to debug page with runId
  const handleDebug = () => {
    if (runId) {
      navigate(`/debug?execution=${runId}&type=v2`);
    }
  };

  // Get status badge
  const getStatusBadge = () => {
    switch (executionStatus) {
      case 'running':
        return (
          <span className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></span>
            Running
          </span>
        );
      case 'awaiting_user_input':
        return (
          <span className="flex items-center gap-1.5 px-2.5 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
            <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full"></span>
            Awaiting Input
          </span>
        );
      case 'completed':
        return (
          <span className="flex items-center gap-1.5 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
            Completed
          </span>
        );
      case 'failed':
        return (
          <span className="flex items-center gap-1.5 px-2.5 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
            Failed
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
            <span className="w-1.5 h-1.5 bg-gray-500 rounded-full"></span>
            Ready
          </span>
        );
    }
  };

  // Check if there's sales order data in messages that can be reviewed
  const hasSalesOrderDataInMessages = () => {
    if (!messages || messages.length === 0) return false;

    // Check if sales order was already created
    const salesOrderAlreadyCreated = messages.some(msg => {
      try {
        let content = msg.content;
        if (typeof content === 'string') {
          const jsonMatch = content.match(/\{.*\}/s);
          if (jsonMatch) {
            content = JSON.parse(jsonMatch[0]);
          } else {
            return content.includes('Sales order created successfully') ||
                   content.includes('sales_order_creation_successful') ||
                   content.includes('NetSuite Sales Order ID:') ||
                   content.includes('netsuite_url');
          }
        }
        if (typeof content === 'object' && content !== null) {
          return content.next_action === 'sales_order_created' ||
                 content.netsuite_url ||
                 (content.message && content.message.includes('Sales order created successfully'));
        }
      } catch (e) {
        return false;
      }
      return false;
    });

    // If sales order already created, don't show Review button
    if (salesOrderAlreadyCreated) {
      console.log('🚫 hasSalesOrderDataInMessages: Sales order already created - hiding Review button');
      return false;
    }

    // Check if there's a PreSalesOrderCreationModal (pending review)
    return messages.some(msg => {
      try {
        let content = msg.content;
        if (typeof content === 'string') {
          const trimmed = content.trim();
          if (trimmed.startsWith('{')) {
            content = JSON.parse(trimmed);
          }
        }
        // Check for PreSalesOrderCreationModal component with line_items
        // line_items can be in sales_order_data OR component_definition
        const isPreSalesModal = content?.component_definition?.metadata?.name === 'PreSalesOrderCreationModal';
        const hasLineItems = content?.sales_order_data?.line_items || content?.component_definition?.line_items;
        return isPreSalesModal && hasLineItems;
      } catch (e) {
        return false;
      }
    });
  };

  // Handle Review button click
  const handleReviewClick = () => {
    console.log('🔵 Review button clicked. runId:', runId, 'draftData:', draftData, 'executionStatus:', executionStatus);

    // If no draftData, extract sales order data from messages
    if (!draftData) {
      const salesOrderMsg = messages.find(msg => {
        try {
          let content = msg.content;
          if (typeof content === 'string') {
            const trimmed = content.trim();
            if (trimmed.startsWith('{')) {
              content = JSON.parse(trimmed);
            }
          }
          return content?.component_definition?.metadata?.name === 'PreSalesOrderCreationModal';
        } catch (e) {
          return false;
        }
      });

      if (salesOrderMsg) {
        let content = salesOrderMsg.content;
        if (typeof content === 'string') {
          content = JSON.parse(content.trim());
        }
        // Extract sales order data - can be in sales_order_data OR component_definition
        const extractedData = content.sales_order_data || content.component_definition;
        const dataWithRunId = {
          ...extractedData,
          runId: runId,
          workflowId: workflowId
        };

        console.log('🔵 Extracted sales order data from messages:', dataWithRunId);
        setDraftData(dataWithRunId);
        // Give React a moment to update draftData state before triggering
        setTimeout(() => {
          setIsResuming(true);
          setResumeTrigger(prev => prev + 1);
        }, 50);
        return;
      }
    }

    setIsResuming(true);
    setResumeTrigger(prev => prev + 1);
  };

  if (!token) {
    return (
      <MainLayout title="Sale Order Agent">
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Authentication Required
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Please log in to access the Sale Order Agent.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
            >
              Go to Login
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Sale Order Agent">
      {/* Header - Sale Order themed */}
      <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-t-lg">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3">
            <div className="relative p-2.5 bg-white/20 rounded-xl">
              <Package className="w-7 h-7 text-white" />
              <div className="absolute -bottom-1 -right-1 p-0.5 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full">
                <Zap className="w-2.5 h-2.5 text-white" />
              </div>
            </div>
            <div>
              <h1 className="text-lg font-semibold">{workflowName}</h1>
              <p className="text-xs opacity-90">Purchase Order to Sales Order</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {getStatusBadge()}

          {currentStep && (
            <span className="text-xs opacity-75 bg-white/10 px-2 py-1 rounded">
              Step: {currentStep}
            </span>
          )}

          {runId && (
            <span className="text-xs opacity-75 bg-white/10 px-2 py-1 rounded font-mono">
              {runId.slice(0, 8)}...
            </span>
          )}

          {/* Review Button - shows when there's pending sales order data */}
          {runId && executionStatus !== 'completed' && (draftData || hasSalesOrderDataInMessages()) && (
            <button
              onClick={handleReviewClick}
              disabled={isResuming}
              className="flex items-center gap-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={draftData ? "Review Draft" : "Review Sales Order"}
            >
              <PlayCircle className="w-4 h-4" />
              <span className="text-sm font-medium">{isResuming ? 'Reviewing...' : 'Review'}</span>
            </button>
          )}

          <button
            onClick={() => setShowEventLog(!showEventLog)}
            className={`p-2 rounded-lg transition-colors ${
              showEventLog ? 'bg-white/30' : 'bg-white/20 hover:bg-white/30'
            }`}
            title="Toggle Event Log"
          >
            <Activity className="w-5 h-5" />
          </button>

          {runId && (
            <button
              onClick={handleDebug}
              className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
              title="View Debug Logs"
            >
              <Bug className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden h-[calc(100vh-12rem)] bg-white dark:bg-gray-900 rounded-b-lg">
        {/* Chat Area */}
        <div className="flex-1 flex flex-col">
          {/* Messages - Using SaleV2ChatMessages for Sales Order specific handling */}
          <div className="flex-1 overflow-y-auto">
            <SaleV2ChatMessages
              key={runId || 'initial'}
              messages={messages}
              isLoading={isLoading}
              workflowName={workflowName}
              error={error}
              runId={runId}
              workflowId={workflowId}
              token={token}
              sendMessage={sendMessage}
              executionStatus={executionStatus}
              currentStep={currentStep}
              draftSalesOrderData={draftData}
              resumeTrigger={resumeTrigger}
              setIsResuming={setIsResuming}
              isReopenedSession={!!initialRunId}
              stopPolling={stopPolling}
              resumePolling={resumePolling}
            />
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <V2ChatInput
              onSendMessage={sendMessage}
              isLoading={isLoading}
              placeholder="Upload a purchase order or send a message..."
              disabled={!token}
            />
          </div>
        </div>

        {/* Event Log Sidebar (Optional) */}
        {showEventLog && (
          <div className="w-96 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <V2EventLog
              events={events}
              runId={runId}
              onClose={() => setShowEventLog(false)}
            />
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default SaleOrderAgentChat;
